import { useCallback, useEffect, useRef, useState } from "react";
import { getSaga } from "../services/saga";
import { isTerminal, type SagaRunResult } from "../types/saga";

export interface UseSagaPollingResult {
  saga: SagaRunResult | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Polling 2s enquanto saga não estiver em status terminal (SUCCESS/FAILED/FAILED_NEEDS_MANUAL).
 * Cleanup obrigatório em unmount + para no terminal status.
 */
export function useSagaPolling(sagaId: string, intervalMs = 2000): UseSagaPollingResult {
  const [saga, setSaga] = useState<SagaRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const aliveRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const data = await getSaga(sagaId);
      if (!aliveRef.current) return;
      setSaga(data);
      setError(null);
      // Se atingiu terminal, parar o polling
      if (isTerminal(data.status) && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e instanceof Error ? e.message : "Falha ao carregar saga");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [sagaId]);

  useEffect(() => {
    aliveRef.current = true;
    const load = async () => {
      setLoading(true);
      await fetchOnce();
    };
    void load();
    timerRef.current = setInterval(() => {
      void fetchOnce();
    }, intervalMs);
    return () => {
      aliveRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchOnce, intervalMs]);

  return { saga, loading, error, refresh: fetchOnce };
}
